import React from "react";
import { BookOpen, HandHeart, Mic2, LifeBuoy, GraduationCap, Star } from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section, { SectionHeading } from "@/components/ui/Section";
import Card from "@/components/ui/Card";

const APP_STORE_URL = "https://apps.apple.com/es/app/id6762877703";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=es.diocesishuelva.evangelizaciondigital";

// Distintivo de la App Store
function AppStoreBadge() {
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Descargar en App Store"
      className="inline-block transition-transform duration-fast ease-out-apple hover:scale-105 active:scale-95 focus-ring rounded-lg"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="160" height="54" viewBox="0 0 135 40" aria-hidden="true" focusable="false">
        <rect width="135" height="40" rx="7" fill="#000" />
        <text x="38" y="15" fontSize="8" fill="#fff" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif" letterSpacing="0.5">Download on the</text>
        <text x="35" y="27" fontSize="14" fill="#fff" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif" fontWeight="600" letterSpacing="0.3">App Store</text>
        <path d="M16.5 9.8c1.1-1.4 1.8-3.2 1.6-5-1.6.1-3.5 1.1-4.6 2.4-1 1.2-1.9 3-1.6 4.8 1.7.1 3.5-.9 4.6-2.2zm1.6 2.6c-2.5-.1-4.7 1.4-5.9 1.4-1.2 0-3-.3-3-.3s-3.9 2.3-3.9 8.7c0 5.4 3.2 11.7 4.9 11.7.8 0 2.2-1.1 3.7-1.1 1.5 0 2.7 1.1 3.7 1.1 1.9 0 5.3-7.1 5.3-11.7 0-.1-4.8-2.1-4.8-7.8z" fill="#fff" />
      </svg>
    </a>
  );
}

// Distintivo de Google Play
function GooglePlayBadge() {
  return (
    <a
      href={GOOGLE_PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Descargar en Google Play"
      className="inline-block transition-transform duration-fast ease-out-apple hover:scale-105 active:scale-95 focus-ring rounded-lg"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="160" height="54" viewBox="0 0 135 40" aria-hidden="true" focusable="false">
        <rect width="135" height="40" rx="7" fill="#000" />
        <text x="38" y="15" fontSize="8" fill="#fff" fontFamily="sans-serif" letterSpacing="0.5">GET IT ON</text>
        <text x="35" y="27" fontSize="14" fill="#fff" fontFamily="sans-serif" fontWeight="600" letterSpacing="0.3">Google Play</text>
        <path d="M10 8.5l13 9-13 9V8.5z" fill="#4CAF50" />
        <path d="M10 8.5l7 7-7 11V8.5z" fill="#2196F3" />
        <path d="M23 17.5l3.5 2.5-3.5 2.5-6-4.5 6-4.5 6 4.5-6 4.5z" fill="#FFC107" />
        <path d="M10 26.5l7-7 6 4.5-13 2.5z" fill="#F44336" />
      </svg>
    </a>
  );
}

const features = [
  { icon: BookOpen, emoji: "📖", title: "Palabra del Día", description: "El Evangelio diario con imagen y reflexión para comenzar tu jornada con Cristo.", color: "bg-blue-50 text-blue-600" },
  { icon: HandHeart, emoji: "🙏", title: "Oraciones", description: "Biblioteca de oraciones para cada momento: mañana, noche, intercesión y más.", color: "bg-purple-50 text-purple-600" },
  { icon: Mic2, emoji: "🎙️", title: "Podcast", description: "Reflexiones y homilías en audio. Escucha mientras caminas, conduces o descansas.", color: "bg-orange-50 text-orange-600" },
  { icon: LifeBuoy, emoji: "🤝", title: "Solicitudes de ayuda", description: "Pide ayuda con tus proyectos y dudas, o echa una mano a otros miembros de la comunidad.", color: "bg-green-50 text-green-600" },
  { icon: GraduationCap, emoji: "🎓", title: "Formación", description: "Cursos y recursos para crecer en la fe. Aprende a evangelizar en el mundo digital.", color: "bg-amber-50 text-amber-600" },
  { icon: Star, emoji: "⭐", title: "Misionero Digital", description: "Únete al equipo de evangelización de la Diócesis de Huelva. Juntos llegamos más lejos.", color: "bg-rose-50 text-rose-600" },
];

function DownloadButtons({ className = "" }) {
  return (
    <div className={`flex flex-col sm:flex-row items-center gap-4 ${className}`}>
      <AppStoreBadge />
      <GooglePlayBadge />
    </div>
  );
}

export default function DescargaApp() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Descarga la app"
        description="La app de Evangelización Digital de la Diócesis de Huelva: Palabra del Día, oraciones, podcast, formación y chat. Gratis para iOS y Android."
      />

      {/* Héroe. Es el último de los cuatro que se dibujaban a mano; ahora
          reutiliza PageHero, que ya trae parallax, entrada escalonada y el
          espacio reservado bajo el navbar fijo. */}
      <PageHero
        content={{
          subtitle: "📱 Disponible en iOS y Android",
          title: "La app de Evangelización Digital",
          title_italic: "en tu bolsillo",
          description: "Accede a la Palabra del Día, oraciones, podcast y conecta con la Delegación — desde tu móvil.",
        }}
      >
        <DownloadButtons />
        <p className="mt-4 text-xs text-white/50">Gratis · Sin publicidad · Hecho con fe en Huelva</p>
      </PageHero>

      {/* Características */}
      <Section tone="light" innerClassName="max-w-5xl">
        <Reveal>
          <SectionHeading
            title="Todo lo que necesitas para tu vida de fe"
            description="Seis herramientas espirituales en una sola app, diseñadas para acompañarte cada día."
            align="center"
          />
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <Reveal key={feat.title} delay={Math.min(i, 5) * 70} distance={24}>
                <Card className="p-6 h-full" interactive>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${feat.color}`}>
                    <Icon className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">
                    <span aria-hidden="true">{feat.emoji}</span> {feat.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feat.description}</p>
                </Card>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* Llamada final a la descarga */}
      <Section tone="muted" width="narrow" innerClassName="text-center max-w-2xl">
        <Reveal>
          <div className="text-4xl mb-4" aria-hidden="true">✝️</div>
          <h2 className="font-heading font-bold mb-4 text-balance" style={{ fontSize: "var(--text-h2)" }}>
            Descarga la app ahora
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
            Más de mil fieles ya acompañan su fe con Evangelización Digital. Únete a la comunidad.
          </p>
          <DownloadButtons className="justify-center" />
          <p className="mt-6 text-xs text-muted-foreground">
            Compatible con iPhone (iOS 15+) y Android (7.0+)
          </p>
        </Reveal>
      </Section>
    </div>
  );
}
